import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Loader2, Printer, Download } from 'lucide-react';
import { toast } from 'sonner';
import { seminarApi } from '../../lib/api';
import { downloadBlob } from '../../lib/utils';

/**
 * Dialog zur Auswahl der Datenspalten für den Teilnehmerlisten-PDF-Export.
 * Keine Unterschriften-Spalte – dafür gibt es die Anwesenheitsliste.
 */
export function ParticipantListExportDialog({ seminarId, courseNumber, open, onOpenChange }) {
    const [fields, setFields] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        seminarApi.participantListFields()
            .then((r) => {
                setFields(r.data.fields || []);
                setSelected(r.data.defaults || []);
            })
            .catch(() => toast.error('Feldliste konnte nicht geladen werden'))
            .finally(() => setLoading(false));
    }, [open]);

    const toggle = (key) => setSelected((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

    const selectAll = () => setSelected(fields.map((f) => f.key));
    const selectDefault = () => setSelected(['first_name', 'last_name', 'email']);

    const handleDownload = async () => {
        if (!selected.length) { toast.error('Bitte mindestens ein Feld auswählen'); return; }
        setDownloading(true);
        try {
            const r = await seminarApi.participantListPdf(seminarId, selected);
            downloadBlob(new Blob([r.data]), `Teilnehmerliste_${courseNumber || seminarId}.pdf`);
            onOpenChange(false);
        } catch {
            toast.error('PDF konnte nicht erstellt werden');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-testid="participant-list-export-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Printer className="h-5 w-5" />
                        Teilnehmerliste – Felder auswählen
                    </DialogTitle>
                    <DialogDescription>
                        Wählen Sie die Spalten, die in die PDF übernommen werden sollen. Für Unterschriften-Listen nutzen Sie bitte die <strong>Anwesenheitsliste</strong>.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-xs">
                            <button type="button" className="text-primary hover:underline" onClick={selectAll} data-testid="participant-list-select-all">Alle auswählen</button>
                            <span className="text-slate-400">·</span>
                            <button type="button" className="text-primary hover:underline" onClick={selectDefault} data-testid="participant-list-select-default">Standard (Vorname · Nachname · E-Mail)</button>
                            <span className="text-slate-400">·</span>
                            <button type="button" className="text-primary hover:underline" onClick={() => setSelected([])} data-testid="participant-list-select-none">Keine</button>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-[50vh] overflow-auto pr-1">
                            {fields.map((f) => (
                                <label key={f.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(f.key)}
                                        onChange={() => toggle(f.key)}
                                        className="accent-primary"
                                        data-testid={`field-${f.key}`}
                                    />
                                    <span className="text-sm">{f.label}</span>
                                </label>
                            ))}
                        </div>
                        <p className="text-[11px] text-slate-500">
                            Bei mehr als 5 Spalten wird die PDF automatisch im Querformat erstellt.
                        </p>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                    <Button onClick={handleDownload} disabled={downloading || loading || !selected.length} data-testid="participant-list-download">
                        {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                        PDF herunterladen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
